/**
 * PDF Editor — annotate locally, send envelopes via NDSign.
 *
 * Intent: Engineering staff preparing a PDF, then routing it for sequential e-signature.
 * Palette: Deep Ocean / Sand (Nautilus Console). Depth: borders-only.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Drawer,
  Divider,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  CloudUpload as CloudUploadIcon,
  Draw as DrawIcon,
  Inbox as InboxIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { PDFViewer, PDFViewerRef, ExportPlugin } from '@embedpdf/react-pdf-viewer';
import { useAuth } from '../contexts/AuthContext';
import ndsignService, {
  CreateEnvelopeField,
  CreateEnvelopeSigner,
  NdSignColleague,
  NdSignEnvelope,
} from '../services/ndsignService';
import FieldOverlay, { pagePointFromContainer } from '../components/pdfEditor/FieldOverlay';
import PrepareFieldsPanel from '../components/pdfEditor/PrepareFieldsPanel';
import {
  DEFAULT_FIELD_SIZE,
  signerColor,
  type FieldType,
  type OverlayField,
} from '../components/pdfEditor/fieldTypes';
import { usePdfLayout } from '../components/pdfEditor/usePdfLayout';

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NDSIGN_APP_URL = 'https://nd-sign.nautilusdynamics.com';
const ENVELOPE_LIST_LIMIT = 6;

const newFieldId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `field-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

type SignerDraft = {
  key: string;
  name: string;
  email: string;
};

const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'signed':
      return 'success';
    case 'sent':
    case 'active':
      return 'info';
    case 'declined':
    case 'voided':
    case 'expired':
      return 'error';
    default:
      return 'warning';
  }
};

const PdfEditorPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const annotationAuthor = user?.name?.trim() || user?.trigram?.trim() || 'User';
  const myEmail = (user?.email || '').trim().toLowerCase();

  const viewerRef = useRef<PDFViewerRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pdfSrcRef = useRef<string | null>(null);
  const dragDepthRef = useRef(0);

  const [pdfSrc, setPdfSrc] = useState<string | null>(null);
  const [filename, setFilename] = useState('document.pdf');
  const [viewerReady, setViewerReady] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const [envelopesOpen, setEnvelopesOpen] = useState(false);
  const [envelopes, setEnvelopes] = useState<NdSignEnvelope[]>([]);
  const [envelopesLoading, setEnvelopesLoading] = useState(false);
  const [envelopesError, setEnvelopesError] = useState<string | null>(null);

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTitle, setSendTitle] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [expiresDays, setExpiresDays] = useState(14);
  const [signers, setSigners] = useState<SignerDraft[]>([]);
  const [colleagues, setColleagues] = useState<NdSignColleague[]>([]);
  const [sending, setSending] = useState(false);

  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [signEnvelopeId, setSignEnvelopeId] = useState<number | null>(null);

  const [prepareMode, setPrepareMode] = useState(false);
  const [fields, setFields] = useState<OverlayField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [activeSignerKey, setActiveSignerKey] = useState<string | null>(null);
  const [armedType, setArmedType] = useState<FieldType | null>(null);

  const layout = usePdfLayout(wrapperRef, pdfSrc);

  const consoleColors = {
    surface: isDark ? '#0f2134' : '#ffffff',
    border: isDark ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)',
    accent: '#ccba9b',
    textPrimary: isDark ? '#ffffff' : '#0f2134',
    textSecondary: isDark ? '#b0bec5' : '#546e7a',
    borderStrong: isDark ? 'rgba(204, 186, 155, 0.3)' : 'rgba(15, 33, 52, 0.2)',
  };

  const revokePdfSrc = useCallback((src: string | null) => {
    if (src?.startsWith('blob:')) {
      URL.revokeObjectURL(src);
    }
  }, []);

  useEffect(() => {
    pdfSrcRef.current = pdfSrc;
  }, [pdfSrc]);

  useEffect(() => {
    return () => {
      revokePdfSrc(pdfSrcRef.current);
    };
  }, [revokePdfSrc]);

  useEffect(() => {
    if (!viewerRef.current?.container) return;
    viewerRef.current.container.setTheme({ preference: isDark ? 'dark' : 'light' });
  }, [isDark, pdfSrc, viewerReady]);

  const loadEnvelopes = useCallback(async () => {
    setEnvelopesLoading(true);
    setEnvelopesError(null);
    try {
      const list = await ndsignService.listEnvelopes();
      setEnvelopes(list.slice(0, ENVELOPE_LIST_LIMIT));
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Failed to load envelopes');
      setEnvelopesError(message);
    } finally {
      setEnvelopesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (envelopesOpen) {
      void loadEnvelopes();
    }
  }, [envelopesOpen, loadEnvelopes]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      const type = (data as { type?: string }).type;
      if (type !== 'ndsign:complete' && type !== 'ndsign:decline' && type !== 'ndsign:session') {
        return;
      }
      if (type === 'ndsign:complete' || type === 'ndsign:decline') {
        if (signEnvelopeId != null) {
          void ndsignService.refreshEnvelope(signEnvelopeId).then((updated) => {
            setEnvelopes((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
          }).catch(() => undefined);
        } else {
          void loadEnvelopes();
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [signEnvelopeId, loadEnvelopes]);

  useEffect(() => {
    if (!prepareMode) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedFieldId(null);
        setArmedType(null);
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedFieldId && !sendOpen) {
        const target = event.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          setFields((prev) => prev.filter((f) => f.id !== selectedFieldId));
          setSelectedFieldId(null);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prepareMode, selectedFieldId, sendOpen]);

  const loadPdfFile = useCallback(
    (file: File) => {
      if (!isPdfFile(file)) {
        setError('Please select a PDF file.');
        return;
      }

      setError(null);
      setViewerReady(false);
      setPrepareMode(false);
      setFields([]);
      setSelectedFieldId(null);
      setArmedType(null);
      setActiveSignerKey(null);
      revokePdfSrc(pdfSrcRef.current);
      setFilename(file.name);
      setPdfSrc(URL.createObjectURL(file));
    },
    [revokePdfSrc]
  );

  const handleOpenFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    loadPdfFile(file);
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (event.dataTransfer.types.includes('Files')) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setDragActive(false);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);

    const file = Array.from(event.dataTransfer.files).find(isPdfFile);
    if (!file) {
      setError('Please drop a PDF file.');
      return;
    }
    if (prepareMode) {
      setError('Finish placing signature spaces before opening another PDF.');
      return;
    }
    loadPdfFile(file);
  };

  const exportPdfBytes = useCallback(async (): Promise<ArrayBuffer> => {
    const registry = await viewerRef.current?.registry;
    if (!registry) {
      throw new Error('PDF viewer is not ready yet. Please wait a moment and try again.');
    }

    const exportPlugin = registry.getPlugin<ExportPlugin>('export')?.provides();
    if (!exportPlugin) {
      throw new Error('Export is unavailable in the PDF viewer.');
    }

    return exportPlugin.saveAsCopy().toPromise();
  }, []);

  const handleDownload = useCallback(async () => {
    setError(null);
    setDownloading(true);
    try {
      const arrayBuffer = await exportPdfBytes();
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to download PDF';
      setError(message);
    } finally {
      setDownloading(false);
    }
  }, [exportPdfBytes, filename]);

  const openSendDialog = async () => {
    setError(null);
    if (!sendTitle) {
      setSendTitle(filename.replace(/\.pdf$/i, '') || 'Envelope');
    }
    if (signers.length === 0) {
      const self: SignerDraft[] = myEmail
        ? [{ key: `self-${myEmail}`, name: user?.name || myEmail.split('@')[0], email: myEmail }]
        : [];
      setSigners(self);
    }
    setSendOpen(true);
    try {
      const list = await ndsignService.getColleagues();
      setColleagues(list);
    } catch {
      setColleagues([]);
    }
  };

  const beginPrepare = () => {
    if (signers.length === 0) {
      setError('Add at least one signer.');
      return;
    }
    setError(null);
    setFields((prev) => prev.filter((f) => signers.some((s) => s.key === f.signerId)));
    setActiveSignerKey(signers[0]?.key ?? null);
    setSelectedFieldId(null);
    setArmedType(null);
    setSendOpen(false);
    setPrepareMode(true);
  };

  const exitPrepare = () => {
    setPrepareMode(false);
    setArmedType(null);
    setSelectedFieldId(null);
  };

  const addSignerFromColleague = (_: unknown, value: NdSignColleague | string | null) => {
    if (!value) return;
    if (typeof value === 'string') {
      const email = value.trim().toLowerCase();
      if (!EMAIL_RE.test(email)) {
        setError('Enter a valid email address.');
        return;
      }
      if (signers.some((s) => s.email === email)) return;
      setSigners((prev) => [
        ...prev,
        { key: `ext-${email}`, name: email.split('@')[0], email },
      ]);
      return;
    }
    const email = value.email.trim().toLowerCase();
    if (signers.some((s) => s.email === email)) return;
    setSigners((prev) => [
      ...prev,
      { key: `user-${value.id}`, name: value.name, email },
    ]);
  };

  const moveSigner = (index: number, direction: -1 | 1) => {
    setSigners((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const removeSigner = (key: string) => {
    setSigners((prev) => prev.filter((s) => s.key !== key));
  };

  const handleSend = async (includeFields: boolean) => {
    if (signers.length === 0) {
      setError('Add at least one signer.');
      return;
    }
    const payloadFields: CreateEnvelopeField[] = includeFields
      ? fields
          .map((f) => {
            const signer_index = signers.findIndex((s) => s.key === f.signerId);
            return {
              signer_index,
              type: f.type,
              page: f.page,
              x: Math.round(f.x * 100) / 100,
              y: Math.round(f.y * 100) / 100,
              w: Math.round(f.w * 100) / 100,
              h: Math.round(f.h * 100) / 100,
              label: f.label || '',
            };
          })
          .filter((f) => f.signer_index >= 0)
      : [];
    if (payloadFields.length > 0) {
      const missing = signers.filter((_, i) => !payloadFields.some((f) => f.signer_index === i));
      if (missing.length > 0) {
        setError(
          `${missing.map((s) => s.name).join(', ')} ${
            missing.length === 1 ? 'has' : 'have'
          } no fields. Place at least one space per signer, or send without fields.`
        );
        return;
      }
    }
    setSending(true);
    setError(null);
    try {
      const arrayBuffer = await exportPdfBytes();
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const payloadSigners: CreateEnvelopeSigner[] = signers.map((s, i) => ({
        name: s.name,
        email: s.email,
        order: i + 1,
      }));
      const envelope = await ndsignService.createEnvelope({
        pdf: blob,
        filename,
        title: sendTitle.trim() || filename,
        message: sendMessage.trim() || undefined,
        expiresDays,
        signers: payloadSigners,
        fields: payloadFields,
      });
      setSendOpen(false);
      exitPrepare();
      setFields([]);
      setEnvelopes((prev) => [envelope, ...prev.filter((e) => e.id !== envelope.id)]);
      setEnvelopesOpen(true);
      if (envelope.my_embed_url) {
        setSignEnvelopeId(envelope.id);
        setSignUrl(envelope.my_embed_url);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Failed to create envelope');
      setError(message);
    } finally {
      setSending(false);
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!armedType || !wrapperRef.current) return;
    const point = pagePointFromContainer(layout, wrapperRef.current, event.clientX, event.clientY);
    if (!point) return;
    if (!activeSignerKey) {
      setError('Select a signer first.');
      return;
    }
    const size = DEFAULT_FIELD_SIZE[armedType];
    const pageDim = layout.pages[point.page];
    const w = Math.min(size.w, pageDim?.w ?? size.w);
    const h = Math.min(size.h, pageDim?.h ?? size.h);
    setFields((prev) => [
      ...prev,
      {
        id: newFieldId(),
        signerId: activeSignerKey,
        type: armedType,
        page: point.page,
        x: Math.max(0, Math.min(point.x - w / 2, (pageDim?.w ?? size.w) - w)),
        y: Math.max(0, Math.min(point.y - h / 2, (pageDim?.h ?? size.h) - h)),
        w,
        h,
        label: '',
      },
    ]);
    setArmedType(null);
    event.stopPropagation();
  };

  const handlePlaceLayerWheel = (event: React.WheelEvent) => {
    const scroller = layout.getScroller();
    if (!scroller) return;
    scroller.scrollTop += event.deltaY;
    scroller.scrollLeft += event.deltaX;
  };

  const signerColorOf = useCallback(
    (signerId: string) => signerColor(signers.findIndex((s) => s.key === signerId)),
    [signers]
  );

  const handleFieldMove = useCallback((id: string, x: number, y: number, page: number) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, x, y, page } : f)));
  }, []);

  const handleFieldResize = useCallback((id: string, w: number, h: number) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, w, h } : f)));
  }, []);

  const handleFieldDelete = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    setSelectedFieldId(null);
  }, []);

  const openSign = (envelope: NdSignEnvelope) => {
    if (!envelope.my_embed_url) return;
    // #region agent log
    fetch('http://127.0.0.1:7674/ingest/b94f3b1d-59d3-42eb-b001-33ab5638a3df',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'5ee614'},body:JSON.stringify({sessionId:'5ee614',runId:'pre-fix',hypothesisId:'A',location:'PdfEditorPage.tsx:openSign',message:'opening_cached_embed',data:{envelopeStatus:envelope.status,hasEmbed:Boolean(envelope.my_embed_url),embedLen:(envelope.my_embed_url||'').length,signerStatuses:envelope.signers.map((s)=>s.status),viewerIsLater:envelope.signers.findIndex((s)=>s.status==='active')>0},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    setSignEnvelopeId(envelope.id);
    setSignUrl(envelope.my_embed_url);
  };

  const handleRefreshOne = async (id: number) => {
    try {
      const updated = await ndsignService.refreshEnvelope(id);
      setEnvelopes((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setEnvelopes((prev) => prev.filter((e) => e.id !== id));
        return;
      }
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Refresh failed';
      setEnvelopesError(message);
    }
  };

  const handleDownloadSigned = async (envelope: NdSignEnvelope) => {
    try {
      await ndsignService.downloadDocument(
        envelope.id,
        `signed-${envelope.doc_filename || 'document.pdf'}`
      );
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Download failed — envelope may not be completed yet';
      setEnvelopesError(message);
    }
  };

  const colleagueOptions = useMemo(
    () => colleagues.filter((c) => !signers.some((s) => s.email === c.email)),
    [colleagues, signers]
  );

  const outlinedBtnSx = {
    color: consoleColors.textPrimary,
    borderColor: consoleColors.borderStrong,
    '&:hover': {
      borderColor: consoleColors.accent,
      bgcolor: alpha(consoleColors.accent, 0.08),
    },
  };

  const containedBtnSx = {
    bgcolor: consoleColors.accent,
    color: '#0f2134',
    '&:hover': { bgcolor: alpha(consoleColors.accent, 0.85) },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 100px)',
        minHeight: 480,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 0.5,
        }}
      >
        <PictureAsPdfIcon sx={{ color: consoleColors.accent }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: consoleColors.textPrimary,
            mr: 'auto',
          }}
        >
          {prepareMode ? 'Place signature spaces' : 'PDF Editor'}
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={handleFileSelect}
        />

        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
          onClick={handleOpenFile}
          disabled={prepareMode || sending}
          sx={outlinedBtnSx}
        >
          Open PDF
        </Button>

        <Button
          size="small"
          variant="contained"
          startIcon={
            downloading ? (
              <CircularProgress size={16} sx={{ color: '#0f2134' }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={() => void handleDownload()}
          disabled={!pdfSrc || !viewerReady || downloading || prepareMode}
          sx={containedBtnSx}
        >
          Download
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<DrawIcon sx={{ fontSize: 16 }} />}
          onClick={() => void openSendDialog()}
          disabled={!pdfSrc || !viewerReady || sending || prepareMode}
          sx={outlinedBtnSx}
        >
          Send for signature
        </Button>

        <Button
          size="small"
          variant={envelopesOpen ? 'contained' : 'outlined'}
          startIcon={<InboxIcon sx={{ fontSize: 16 }} />}
          onClick={() => setEnvelopesOpen((v) => !v)}
          sx={envelopesOpen ? containedBtnSx : outlinedBtnSx}
        >
          Envelopes
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: prepareMode ? 'row' : 'column',
          border: `2px dashed ${dragActive ? consoleColors.accent : consoleColors.borderStrong}`,
          borderRadius: 1,
          bgcolor: dragActive
            ? alpha(consoleColors.accent, 0.1)
            : consoleColors.surface,
          overflow: 'hidden',
          transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        {!pdfSrc ? (
          <Box
            role="button"
            tabIndex={0}
            onClick={handleOpenFile}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpenFile();
              }
            }}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              px: 3,
              textAlign: 'center',
              cursor: 'pointer',
              outline: 'none',
              '&:focus-visible': {
                boxShadow: `inset 0 0 0 2px ${consoleColors.accent}`,
              },
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 56,
                color: dragActive ? consoleColors.accent : consoleColors.textSecondary,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: dragActive ? consoleColors.accent : consoleColors.textPrimary,
                fontWeight: 600,
              }}
            >
              {dragActive ? 'Drop PDF to open' : 'Drag & drop a PDF here'}
            </Typography>
            <Typography variant="body2" sx={{ color: consoleColors.textSecondary, maxWidth: 420 }}>
              Annotate, place signature spaces, then send for signing.
              Or click to browse.
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={(event) => {
                event.stopPropagation();
                handleOpenFile();
              }}
              sx={containedBtnSx}
            >
              Choose PDF
            </Button>
          </Box>
        ) : (
          <>
            {prepareMode ? (
              <PrepareFieldsPanel
                signers={signers}
                fields={fields}
                activeSignerKey={activeSignerKey}
                armedType={armedType}
                sending={sending}
                onSelectSigner={(key) => {
                  setActiveSignerKey(key);
                  setArmedType(null);
                }}
                onArmType={setArmedType}
                onBack={() => {
                  exitPrepare();
                  setSendOpen(true);
                }}
                onSkip={() => void handleSend(false)}
                onSend={() => void handleSend(true)}
              />
            ) : null}
            <Box
              ref={wrapperRef}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                position: 'relative',
                cursor: prepareMode && armedType ? 'crosshair' : 'default',
                outline: prepareMode && armedType ? `2px solid ${consoleColors.accent}` : 'none',
                outlineOffset: -2,
              }}
            >
              {!viewerReady && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(consoleColors.surface, 0.85),
                    zIndex: 4,
                  }}
                >
                  <CircularProgress sx={{ color: consoleColors.accent }} />
                </Box>
              )}
              {dragActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    bgcolor: alpha(consoleColors.surface, 0.92),
                    border: `2px dashed ${consoleColors.accent}`,
                    pointerEvents: 'none',
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: consoleColors.accent }} />
                  <Typography variant="body1" sx={{ color: consoleColors.accent, fontWeight: 600 }}>
                    Drop PDF to replace
                  </Typography>
                </Box>
              )}
              {prepareMode && layout.ready ? (
                <FieldOverlay
                  layout={layout}
                  fields={fields}
                  signerColor={signerColorOf}
                  selectedFieldId={selectedFieldId}
                  onSelect={setSelectedFieldId}
                  onMove={handleFieldMove}
                  onResize={handleFieldResize}
                  onDelete={handleFieldDelete}
                />
              ) : null}
              {prepareMode && armedType ? (
                <Box
                  onClick={handleCanvasClick}
                  onWheel={handlePlaceLayerWheel}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    cursor: 'crosshair',
                  }}
                />
              ) : null}
              <PDFViewer
                ref={viewerRef}
                config={{
                  src: pdfSrc,
                  tabBar: 'multiple',
                  theme: { preference: isDark ? 'dark' : 'light' },
                  export: { defaultFileName: filename },
                  annotations: { annotationAuthor },
                }}
                style={{ width: '100%', height: '100%' }}
                onReady={(registry) => {
                  setViewerReady(true);
                  layout.handleViewerReady(registry);
                }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Send for signature dialog */}
      <Dialog open={sendOpen} onClose={() => !sending && setSendOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600 }}>Send for signature</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Title"
              value={sendTitle}
              onChange={(e) => setSendTitle(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Message (optional)"
              value={sendMessage}
              onChange={(e) => setSendMessage(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <TextField
              label="Expires (days)"
              type="number"
              value={expiresDays}
              onChange={(e) => setExpiresDays(Math.max(1, Math.min(365, Number(e.target.value) || 14)))}
              size="small"
              inputProps={{ min: 1, max: 365 }}
              sx={{ maxWidth: 160 }}
            />

            <Typography variant="body2" color="text.secondary">
              Next you will place signature, initials, date, and name spaces on the document.
              Skip that step to let each signer place their own signature.
            </Typography>

            <Typography
              variant="overline"
              sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
            >
              Signers (order = signing sequence)
            </Typography>

            <Autocomplete
              freeSolo
              options={colleagueOptions}
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : `${opt.name} <${opt.email}>`
              }
              onChange={addSignerFromColleague}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Add colleague or type an email"
                  placeholder="Search or paste email"
                />
              )}
            />

            <Stack spacing={1}>
              {signers.map((s, index) => (
                <Box
                  key={s.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: `1px solid ${consoleColors.border}`,
                    borderRadius: 1,
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <DragIndicatorIcon sx={{ color: consoleColors.textSecondary, fontSize: 18 }} />
                  <Chip
                    size="small"
                    label={String(index + 1).padStart(2, '0')}
                    sx={{ fontFamily: 'monospace', minWidth: 36 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {s.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {s.email}
                    </Typography>
                  </Box>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" disabled={index === 0} onClick={() => moveSigner(index, -1)}>
                        ↑
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === signers.length - 1}
                        onClick={() => moveSigner(index, 1)}
                      >
                        ↓
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => removeSigner(s.key)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {signers.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No signers yet. Add colleagues or external emails above.
                </Typography>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={beginPrepare}
            disabled={sending || signers.length === 0}
            startIcon={<DrawIcon />}
            sx={containedBtnSx}
          >
            Place signature spaces
          </Button>
        </DialogActions>
      </Dialog>

      {/* Envelopes drawer */}
      <Drawer
        anchor="right"
        open={envelopesOpen}
        onClose={() => setEnvelopesOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            bgcolor: consoleColors.surface,
            borderLeft: `1px solid ${consoleColors.border}`,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="overline"
            sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', flex: 1 }}
          >
            Envelopes
          </Typography>
          <Tooltip title="Refresh list">
            <IconButton size="small" onClick={() => void loadEnvelopes()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setEnvelopesOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        {envelopesError && (
          <Alert severity="error" sx={{ m: 1.5 }} onClose={() => setEnvelopesError(null)}>
            {envelopesError}
          </Alert>
        )}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {envelopesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: consoleColors.accent }} />
            </Box>
          ) : envelopes.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No envelopes yet. Open a PDF and use Send for signature.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Divider />}>
              {envelopes.slice(0, ENVELOPE_LIST_LIMIT).map((env) => (
                <Box key={env.id} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {env.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {env.doc_filename || 'document.pdf'}
                      </Typography>
                    </Box>
                    <Chip size="small" label={env.status} color={statusColor(env.status)} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {env.signers.map((s) => `${s.name} (${s.status})`).join(' → ')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {env.my_embed_url && env.status !== 'completed' && env.status !== 'declined' && (
                      <Button size="small" startIcon={<DrawIcon />} onClick={() => openSign(env)}>
                        Sign
                      </Button>
                    )}
                    {env.status === 'completed' && (
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => void handleDownloadSigned(env)}
                      >
                        Download
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={() => void handleRefreshOne(env.id)}
                    >
                      Refresh
                    </Button>
                    <Tooltip title="Open this envelope on NDSign">
                      <IconButton
                        size="small"
                        component="a"
                        href={`${NDSIGN_APP_URL}/envelopes/${env.ndsign_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Button
            component="a"
            href={NDSIGN_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            fullWidth
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={outlinedBtnSx}
          >
            See more on NDSign
          </Button>
        </Box>
      </Drawer>

      {/* Signer iframe */}
      <Dialog
        open={Boolean(signUrl)}
        onClose={() => {
          setSignUrl(null);
          setSignEnvelopeId(null);
          void loadEnvelopes();
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            Sign document
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              setSignUrl(null);
              setSignEnvelopeId(null);
              void loadEnvelopes();
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 0, overflow: 'hidden' }}>
          {signUrl && (
            <Box
              component="iframe"
              title="NDSign signer"
              src={signUrl}
              sx={{ width: '100%', height: '100%', border: 0 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PdfEditorPage;
